<script setup>
// NoteCardModal.vue
import { computed, ref, watch } from "vue";
import { useNoteStore } from "@/stores/useNoteStore";
import { useTextStore } from "@/stores/useTextStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useUserStore } from "@/stores/useUserStore";
import { useSettingStore } from "@/stores/useSettingStore";
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";

import EditNoteForm from "@/components/Note/EditNoteForm.vue";
import DeleteNoteForm from "@/components/Note/DeleteNoteForm.vue";
import UserCardModal from "@/components/User/UserCardModal.vue";
import TagCardModal from "@/components/Tag/TagCardModal.vue";
import TextCardModal from "@/components/Text/TextCardModal.vue";
import ActivityCardModal from "@/components/Activity/ActivityCardModal.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";

import ShareNotePopover from "./ShareNotePopover.vue";
import { useNoteShare } from "@/composables/useNoteShare";
import NoteAttachmentModal from "./NoteAttachmentModal.vue";
import { hasAttachment, getNoteAttachments } from "@/utils/noteLinks";

import { buildHighlightedExcerpt } from "@/utils/stringUtils";
import { buildHtmlHighlightedExcerpt } from "@/utils/htmlExcerpt";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  noteId: { type: Number, required: true },
  userId: { type: Number, required: true },
});

// ✅ FIX: adicionado "note-updated" — emitido quando a nota é editada (card permanece aberto).
const emit = defineEmits(["close", "refresh", "notify", "note-updated"]);

const noteStore = useNoteStore();
const textStore = useTextStore();
const activityStore = useActivityStore();
const userStore = useUserStore();
const settingStore = useSettingStore();
const managedAccountStore = useManagedAccountStore();

const showEditModal = ref(false);
const editInitialStep = ref(1);

// >>> NOVO: modal de anexo (clip) — mesmo comportamento de NoteList.vue
const showAttachmentModal = ref(false);
const attachmentList = ref([]);

function openAttachmentModal() {
  const list = getNoteAttachments(note.value);
  if (!list.length) return;
  attachmentList.value = list;
  showAttachmentModal.value = true;
}

function closeAttachmentModal() {
  showAttachmentModal.value = false;
  attachmentList.value = [];
}

// Estado para controle do DeleteNoteForm
const showDeleteModal = ref(false);

// ✅ FIX: durante a exclusão, a nota some do noteStore (no deleteNoteById) ANTES de o
// card fechar. Para o card não "piscar vazio", congelamos o último estado da nota e o
// usamos como fallback enquanto a exclusão está em andamento. Assim o card permanece
// CHEIO até sumir.
const isDeletingNote = ref(false);
const frozenNote = ref(null);

const {
  isSharePopoverOpen,
  absoluteShareUrl,
  isGenerating,
  isCopied,
  isRevoking,
  shareError,
  shareNoteId,
  openSharePopover,
  generateLink,
  closeSharePopover,
  copyLink,
  shareWhatsApp,
  shareEmail,
  shareNative,
  revokeLink,
} = useNoteShare();

const activityId = ref(null);
const textId = ref(null);
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);
// ✅ FIX: quando a nota não está no store (ex.: aberta via link compartilhado
// antes da lista carregar) e o fetch falha, sem isso o card ficava "Carregando..."
// para sempre — a promise rejeitada nunca chegava a zerar isLoadingData.
const loadError = ref(false);

// ✅ FIX: guarda local da nota buscada via getNoteById. É o fallback do `note`
// computed quando a nota NÃO está em noteStore.notes. Isso acontece ao abrir o
// card por link compartilhado: getNoteById insere a nota no store, mas em seguida
// a busca paginada da atividade (disparada por fetchRelatedData → getActivityById)
// substitui noteStore.notes por uma página que pode NÃO conter esta nota (ex.:
// filtro "minhas notas" exclui notas de outro autor). Sem este fallback, `note`
// voltava a undefined e o card ficava "Carregando..." para sempre.
const fetchedNote = ref(null);

const showUserModal = ref(false);
const selectedUserId = ref(null);

const showTagCardModal = ref(false);
const selectedTagIdForModal = ref(null);

const showTextCardModal = ref(false);
const selectedTextIdForModal = ref(null);

const showActivityCardModal = ref(false);
const selectedActivityIdForModal = ref(null);

const showPermissionView = ref(false);

const breadcrumbTextTitle = ref("");
const breadcrumbTextSubtitle = ref("");
const breadcrumbActivityTitle = ref("");

const currentUserId = computed(() => userStore.currentUser?.userId ?? null);

const note = computed(() => {
  const targetId = Number(props.noteId);

  // Preferência: a versão "viva" do store (reflete edições/tags em tempo real).
  const live = noteStore.notes.find((n) => Number(n.noteId) === targetId);
  if (live) return live;

  // ✅ FIX: fallback para a nota buscada via getNoteById quando o store não a
  // contém (fluxo do link compartilhado — ver comentário em `fetchedNote`).
  if (fetchedNote.value && Number(fetchedNote.value.noteId) === targetId) {
    return fetchedNote.value;
  }

  // ✅ FIX: nota já removida do store, mas exclusão em andamento → mantém o snapshot
  // congelado para o card continuar cheio até o fechamento.
  if (isDeletingNote.value) return frozenNote.value;

  return undefined;
});

const isShareOwner = computed(() => {
  return note.value?.createdByUserId === props.userId;
});

const excerptHighlightStyle = computed(() => {
  if (!note.value) return {};

  const tagIds = note.value.tagIds ?? [];

  if (tagIds.length === 0) {
    const s = settingStore.setting;
    if (!s) return {};

    const textColor = s.uncategorizedTextColor;
    const bgColor = s.uncategorizedBackgroundColor;

    if (isExcerptSingleWord.value) {
      return {
        backgroundColor: "transparent",
        color: textColor,
        fontWeight: "bold",
      };
    }

    return {
      backgroundColor: bgColor,
      color: "inherit",
      fontWeight: "normal",
    };
  }

  const tagIsPrimary = note.value.tagIsPrimary ?? [];

  let primaryIdx = tagIsPrimary.findIndex((v) => v === true);
  if (primaryIdx === -1) primaryIdx = 0;

  const tagIdsLength = tagIds.length;

  function pick(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    if (tagIdsLength > 0 && arr.length === tagIdsLength) {
      return arr[primaryIdx] ?? null;
    }
    return arr[0] ?? null;
  }

  const textColor = pick(note.value.tagSpecificColors) || "#713f12";
  const bgColor = pick(note.value.tagSpecificBackgrounds) || "#fef08a";

  if (isExcerptSingleWord.value) {
    return {
      backgroundColor: "transparent",
      color: textColor,
      fontWeight: "bold",
    };
  }

  return {
    backgroundColor: bgColor,
    color: "inherit",
    fontWeight: "normal",
  };
});

function openUserModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserModal.value = true;
}

function openTagCardByIndex(idx) {
  const currentNote = note.value;
  if (!currentNote) return;
  const ids = currentNote.tagIds;
  if (!ids || !Array.isArray(ids)) return;
  const id = ids[idx];
  if (!id) return;
  selectedTagIdForModal.value = id;
  showTagCardModal.value = true;
}

function closeTagCard() {
  showTagCardModal.value = false;
  selectedTagIdForModal.value = null;
}

function openTextCard() {
  const id = textId.value ?? note.value?.textId ?? null;
  if (!id) return;
  selectedTextIdForModal.value = id;
  showTextCardModal.value = true;
}

function closeTextCard() {
  showTextCardModal.value = false;
  selectedTextIdForModal.value = null;
}

function openActivityCard() {
  const id = activityId.value ?? note.value?.activityId ?? null;
  if (!id) return;
  selectedActivityIdForModal.value = id;
  showActivityCardModal.value = true;
}

function closeActivityCard() {
  showActivityCardModal.value = false;
  selectedActivityIdForModal.value = null;
}

function openPermissionView() {
  showPermissionView.value = true;
}

function closePermissionView() {
  showPermissionView.value = false;
}

const canViewPermissions = computed(() => {
  return (
    Number(props.userId) === Number(note.value?.createdByUserId) ||
    Number(props.userId) === Number(note.value?.textCreatedByUserId) ||
    Number(props.userId) === Number(note.value?.activityCreatedByUserId)
  );
});

const memberNotePermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.notes ?? null
);

const memberCanEditNote = computed(
  () => memberNotePermissions.value === null || memberNotePermissions.value.edit
);

function hasAnyPermission(entity, ...permissionNames) {
  if (!entity || !Array.isArray(entity.userPermissions)) return false;
  const lowerNames = permissionNames.map((p) => p.toLowerCase());
  return entity.userPermissions.some((perm) =>
    lowerNames.includes(perm.permissionName?.toLowerCase())
  );
}

const noteHasEditPermission = computed(() =>
  hasAnyPermission(note.value, "Editar")
);

const isNoteCreator = computed(
  () => Number(props.userId) === Number(note.value?.createdByUserId)
);

const isSysUser = computed(
  () => (userStore.currentUser?.userName ?? "").toLowerCase() === "sys"
);

const canShowEditNote = computed(() => {
  return (
    memberCanEditNote.value &&
    (isNoteCreator.value || isSysUser.value || noteHasEditPermission.value)
  );
});

// Mesma condição do editar para o botão apagar
const canShowDeleteNote = computed(() => canShowEditNote.value);

function openEditModal() {
  editInitialStep.value = 1;
  showEditModal.value = true;
}

function handleEditClose() {
  showEditModal.value = false;
  editInitialStep.value = 1;
  noteStore.getNoteById(props.noteId);
  // ✅ FIX: avisa o pai (NoteGeneralForm) que a nota pode ter sido alterada.
  // O card permanece aberto; o pai apenas marca para recarregar a lista ao fechar.
  emit("note-updated");
}

function openDeleteModal() {
  const currentActivityId = activityId.value ?? note.value?.activityId ?? null;

  if (currentActivityId) {
    activityStore.idActivity = currentActivityId;
  }

  // ✅ FIX: congela o estado atual da nota antes de abrir a confirmação, para que o
  // card continue cheio mesmo depois de a nota sair do store durante a exclusão.
  frozenNote.value = note.value ? { ...note.value } : null;
  isDeletingNote.value = true;

  showDeleteModal.value = true;
}

// Cancelamento: fecha só o delete modal, NoteCardModal continua aberto
function handleDeleteClose() {
  showDeleteModal.value = false;
  // ✅ FIX: exclusão cancelada → descongela e volta a usar a nota viva do store
  isDeletingNote.value = false;
  frozenNote.value = null;
}

// Sucesso (após apagar): fecha o delete modal, avisa o pai para atualizar a lista
// e fecha o NoteCardModal.
function handleDeleteRefresh() {
  showDeleteModal.value = false;
  emit("refresh");
  emit("close");
}

// ✅ FIX: repassa as notificações do DeleteNoteForm para o pai e, no SUCESSO da
// exclusão, fecha o card IMEDIATAMENTE (sem esperar o toast de 2400ms do
// DeleteConfirmModal). Isso evita que o card fique visível e vazio antes de sumir,
// pois a nota some do store assim que o delete conclui.
// O toast de sucesso passa a ser exibido pelo pai (nível que sobrevive ao fechamento).
// Erros NÃO disparam fechamento: o DeleteConfirmModal mantém o card aberto e já
// exibe o toast de erro.
function handleDeleteNotify(payload) {
  emit("notify", payload);

  if (payload?.type === "success") {
    handleDeleteRefresh();
  }
}

async function fetchRelatedData(noteData) {
  if (!noteData) return;

  activityId.value = noteData.activityId ?? null;
  breadcrumbTextTitle.value = noteData.textTitle ?? "";
  breadcrumbTextSubtitle.value = noteData.textSubtitle ?? "";
  breadcrumbActivityTitle.value = noteData.activityTitle ?? "";

  if (activityId.value) {
    const activity = await activityStore.getActivityById(activityId.value);
    if (activity) {
      textId.value = activity.textId ?? null;
      if (!breadcrumbActivityTitle.value) {
        breadcrumbActivityTitle.value =
          activity.title ?? activity.activityTitle ?? "";
      }
    }
  }

  if (textId.value && textId.value > 0) {
    const text = await textStore.getTextById(textId.value);
    if (text) {
      if (!breadcrumbTextTitle.value) {
        breadcrumbTextTitle.value = text.title ?? text.textTitle ?? "";
      }
      if (!breadcrumbTextSubtitle.value) {
        breadcrumbTextSubtitle.value = text.subtitle ?? text.textSubtitle ?? "";
      }
    }
  }
}

watch(
  () => props.noteId,
  (rawNewId) => {
    if (!rawNewId) return;

    const newId = Number(rawNewId);

    isLoadingData.value = true;
    loadError.value = false;
    fetchedNote.value = null;
    breadcrumbTextTitle.value = "";
    breadcrumbTextSubtitle.value = "";
    breadcrumbActivityTitle.value = "";
    activityId.value = null;
    textId.value = null;

    const noteData = noteStore.notes.find((n) => Number(n.noteId) === newId);

    if (noteData) {
      fetchRelatedData(noteData).finally(() => {
        isLoadingData.value = false;
      });
    } else {
      noteStore
        .getNoteById(newId)
        .then((result) => {
          // ✅ FIX: retém a nota buscada. fetchRelatedData dispara a busca
          // paginada da atividade, que pode substituir noteStore.notes por uma
          // página sem esta nota — o fallback em `note` a mantém visível.
          fetchedNote.value = result;
          return fetchRelatedData(result).finally(() => {
            isLoadingData.value = false;
          });
        })
        .catch((error) => {
          // ✅ FIX: sem isto, a rejeição (nota inacessível, erro de rede, etc.)
          // deixava o card preso em "Carregando..." para sempre.
          console.error("❌ Erro ao carregar nota no NoteCardModal:", error);
          loadError.value = true;
          isLoadingData.value = false;
        });
    }
  },
  { immediate: true }
);

// content_format do TEXTO (não da nota) — decide se o trecho é fatiado como
// HTML ou como texto puro. Sem isso, um texto convertido cortaria a marcação
// bruta pelos offsets da PROJEÇÃO, mostrando a tag literalmente na tela.
const isTextHtml = computed(() => textStore.selectedText?.contentFormat === "html");

const selectedTextContent = computed(() => {
  if (!note.value || !textStore.fileContent) return null;

  if (isTextHtml.value) {
    const html = buildHtmlHighlightedExcerpt(
      textStore.fileContent,
      note.value.textRangeStart,
      note.value.textRangeEnd,
      100
    );
    return html && { ...html, isHtml: true };
  }

  const plain = buildHighlightedExcerpt(
    textStore.fileContent,
    note.value.textRangeStart,
    note.value.textRangeEnd,
    100
  );
  return plain && { ...plain, isHtml: false };
});

// Trecho de uma palavra só ganha cor e negrito, sem fundo. Reaproveita o
// excerto já calculado acima em vez de fatiar `fileContent` de novo — é o
// mesmo raciocínio de NotePublicView.vue: contar palavra em cima do HTML
// bruto conta caractere de tag.
const isExcerptSingleWord = computed(() => {
  const content = selectedTextContent.value;
  if (!content) return false;

  const plain = content.isHtml ? content.excerptText : content.excerpt;
  const trimmed = (plain ?? "").trim();
  return trimmed.length > 0 && !/\s/.test(trimmed);
});

const formattedCreatedAt = computed(() => {
  if (!note.value?.createdAt) return "";
  const date = new Date(note.value.createdAt);
  return `${date.toLocaleDateString("pt-BR")} às ${date.toLocaleTimeString(
    "pt-BR",
    { hour: "2-digit", minute: "2-digit" }
  )}`;
});

const formattedUpdatedAt = computed(() => {
  if (!note.value?.updatedAt) return "";
  const date = new Date(note.value.updatedAt);
  return `${date.toLocaleDateString("pt-BR")} às ${date.toLocaleTimeString(
    "pt-BR",
    { hour: "2-digit", minute: "2-digit" }
  )}`;
});

const hasContextBreadcrumb = computed(() => {
  return !!(
    String(breadcrumbTextTitle.value ?? "").trim() ||
    String(breadcrumbActivityTitle.value ?? "").trim()
  );
});

function normalizeAvatarPath(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function getTagStyle(noteData, index) {
  const textColor = noteData?.tagSpecificColors?.[index] || "#111827";
  const bgColor =
    noteData?.tagSpecificBackgrounds?.[index] ?? "var(--bg-active-nav)";

  // Só chamado com `note` (= note.value) no template desta tela — reaproveita
  // o mesmo cálculo de `isExcerptSingleWord`, já ciente de HTML vs texto puro.
  if (isExcerptSingleWord.value) {
    return {
      color: textColor,
      fontWeight: "bold",
      backgroundColor: "transparent",
    };
  }

  return {
    backgroundColor: bgColor,
  };
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="note-card-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes da Nota</h3>

        <div class="modal-header-actions">
          <button
            @click="emit('close')"
            type="button"
            class="modal-close-btn"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      <div v-if="note && !isLoadingData" class="card-content-grid">
        <div class="note-details-col">
          <div class="note-details-inner">
            <div class="note-title-row">
              <h3 class="note-title-bold">
                {{ note.title || "Sem Título" }}
                <span class="title-id">(Id: {{ note.noteId }})</span>
              </h3>

              <div class="note-title-actions">
                <button
                  v-if="canShowEditNote"
                  type="button"
                  class="btn-edit-note tooltip-bottom"
                  data-tooltip="Editar nota"
                  @click.stop="openEditModal"
                >
                  <i class="fas fa-pencil-alt"></i>
                </button>

                <button
                  v-if="canShowDeleteNote"
                  type="button"
                  class="btn-delete-note tooltip-bottom"
                  data-tooltip="Apagar nota"
                  @click.stop="openDeleteModal"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>

                <button
                  v-if="canViewPermissions"
                  type="button"
                  class="btn-note-permissions tooltip-bottom"
                  data-tooltip="Ver permissões"
                  @click.stop="openPermissionView"
                >
                  <i class="fas fa-user-shield"></i>
                </button>

                <button
                  type="button"
                  class="btn-share-modal tooltip-bottom"
                  data-tooltip="Compartilhar nota"
                  @click.stop="
                    openSharePopover(props.noteId, note?.shareToken ?? null)
                  "
                >
                  <i class="fas fa-share-alt"></i>
                </button>

                <button
                  v-if="hasAttachment(note)"
                  type="button"
                  class="btn-attachment-note tooltip-bottom"
                  data-tooltip="Ver anexo"
                  @click.stop="openAttachmentModal"
                >
                  <i class="fas fa-paperclip"></i>
                </button>
              </div>
            </div>

            <label class="form-label text-label mt-3">Conteúdo da Nota</label>

            <blockquote class="note-body-blockquote">
              {{ note.body || "Sem conteúdo" }}
            </blockquote>

            <div class="meta-grid meta-grid-spaced">
              <div class="meta-item meta-item-full">
                <span class="meta-label">Tags</span>

                <div class="meta-value mt-1">
                  <span
                    v-if="!note.tagNames || note.tagNames.length === 0"
                    class="italic text-gray-400"
                  >
                    Nenhuma Tag atribuída
                  </span>

                  <div v-else class="note-tags-line">
                    <i class="fas fa-hashtag tag-hash"></i>

                    <button
                      v-for="(tagName, idx) in note.tagNames"
                      :key="`tag-${note.tagIds?.[idx] ?? idx}`"
                      type="button"
                      class="tag-pill tag-link"
                      :style="getTagStyle(note, idx)"
                      @click.stop="openTagCardByIndex(idx)"
                    >
                      {{ tagName }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr class="content-separator" />

            <div v-if="hasContextBreadcrumb" class="context-block">
              <span class="meta-label">TEXTO E ATIVIDADE</span>

              <div class="footer-context-row">
                <button
                  v-if="breadcrumbTextTitle"
                  type="button"
                  class="meta-chip meta-chip-text meta-chip-link"
                  :title="
                    breadcrumbTextSubtitle
                      ? `${breadcrumbTextTitle}\n(${breadcrumbTextSubtitle})`
                      : breadcrumbTextTitle
                  "
                  @click.stop="openTextCard"
                >
                  <i class="fas fa-book-open context-icon"></i>
                  <span class="meta-chip-label">
                    {{ breadcrumbTextTitle }}
                  </span>
                </button>

                <i
                  v-if="breadcrumbTextTitle && breadcrumbActivityTitle"
                  class="fas fa-chevron-right meta-sep"
                ></i>

                <button
                  v-if="breadcrumbActivityTitle"
                  type="button"
                  class="meta-chip meta-chip-activity meta-chip-link"
                  :title="breadcrumbActivityTitle"
                  @click.stop="openActivityCard"
                >
                  <span class="meta-chip-label">
                    {{ breadcrumbActivityTitle }}
                  </span>
                </button>
              </div>
            </div>

            <div class="meta-grid meta-grid-spaced">
              <div class="meta-item meta-item-full">
                <span class="meta-label">Fonte</span>

                <span class="meta-value source-inline">
                  <i class="fas fa-link source-link-icon"></i>
                  <span>{{ note.source || "Não informada" }}</span>
                </span>
              </div>
            </div>
          </div>

          <div class="footer-info">
            <div class="users-row">
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(note.createdByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getAvatarUrl(note.createdByUserPhotoPath)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getAvatarUrl(
                        note.createdByUserPhotoPath
                      )}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Criador</span>
                  <span class="chip-name">
                    {{ note.createdByUserName || "Não informado" }}
                  </span>
                  <span v-if="formattedCreatedAt" class="chip-date">
                    {{ formattedCreatedAt }}
                  </span>
                </div>
              </div>

              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(note.updatedByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getAvatarUrl(note.updatedByUserPhotoPath)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getAvatarUrl(
                        note.updatedByUserPhotoPath
                      )}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Atualizador</span>
                  <span class="chip-name">
                    {{ note.updatedByUserName || "Não informado" }}
                  </span>
                  <span v-if="formattedUpdatedAt" class="chip-date">
                    {{ formattedUpdatedAt }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="selected-text-col">
          <label class="form-label text-label">
            Texto selecionado na atividade
          </label>

          <blockquote
            class="themed-blockquote"
            :class="{ 'no-content': !selectedTextContent }"
          >
            <template v-if="selectedTextContent">
              <span
                v-if="selectedTextContent.truncatedBefore"
                class="excerpt-ellipsis"
              >
                …
              </span>

              <span
                v-if="selectedTextContent.isHtml"
                class="excerpt-before"
                v-html="selectedTextContent.before"
              ></span>
              <span v-else class="excerpt-before">
                {{ selectedTextContent.before }}
              </span>

              <mark
                v-if="selectedTextContent.isHtml"
                class="excerpt-highlight"
                :style="excerptHighlightStyle"
                v-html="selectedTextContent.excerpt"
              ></mark>
              <mark
                v-else
                class="excerpt-highlight"
                :style="excerptHighlightStyle"
              >
                {{ selectedTextContent.excerpt }}
              </mark>

              <span
                v-if="selectedTextContent.isHtml"
                class="excerpt-after"
                v-html="selectedTextContent.after"
              ></span>
              <span v-else class="excerpt-after">
                {{ selectedTextContent.after }}
              </span>

              <span
                v-if="selectedTextContent.truncatedAfter"
                class="excerpt-ellipsis"
              >
                …
              </span>
            </template>

            <template v-else>
              Nenhum trecho de texto foi selecionado.
            </template>
          </blockquote>
        </div>
      </div>

      <div v-else-if="loadError" class="card-body loading-state">
        <i class="fas fa-triangle-exclamation"></i>
        Não foi possível carregar esta nota. Ela pode ter sido removida ou você
        não tem mais permissão para vê-la.
      </div>

      <div v-else class="card-body loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        Carregando detalhes da nota...
      </div>

    </div>
  </div>

  <ShareNotePopover
    v-if="isSharePopoverOpen"
    :show="isSharePopoverOpen"
    :noteTitle="note?.title ?? ''"
    :shareUrl="absoluteShareUrl"
    :isGenerating="isGenerating"
    :isCopied="isCopied"
    :isRevoking="isRevoking"
    :shareError="shareError"
    :noteId="shareNoteId"
    :isOwner="isShareOwner"
    @close="closeSharePopover"
    @generate="generateLink"
    @copy="copyLink"
    @share-whatsapp="shareWhatsApp"
    @share-email="shareEmail(note?.title ?? '')"
    @share-native="shareNative(note?.title ?? '')"
    @revoke="revokeLink"
  />

  <!-- Sub-modais via Teleport.
       Todos são position: fixed e o painel deste card ganha transform ao ser
       arrastado — um ancestral com transform viraria o containing block deles,
       fazendo o overlay cobrir só o painel e acompanhar o arrasto. Fora do
       painel no DOM, seguem ancorados na viewport. -->
  <Teleport to="body">
    <UserCardModal
      v-if="showUserModal"
      :show="showUserModal"
      :userId="selectedUserId"
      @close="showUserModal = false"
    />

    <TagCardModal
      v-if="showTagCardModal"
      :show="showTagCardModal"
      :tagId="selectedTagIdForModal"
      @close="closeTagCard"
      @tag-updated="noteStore.getNoteById(props.noteId)"
    />

    <TextCardModal
      v-if="showTextCardModal"
      :show="showTextCardModal"
      :textId="selectedTextIdForModal"
      @close="closeTextCard"
    />

    <ActivityCardModal
      v-if="showActivityCardModal"
      :show="showActivityCardModal"
      :activityId="selectedActivityIdForModal"
      :userId="currentUserId"
      @close="closeActivityCard"
      @notify="(payload) => emit('notify', payload)"
    />

    <PermissionView
      v-if="showPermissionView"
      :show="showPermissionView"
      entityType="note"
      :noteId="props.noteId"
      title="Permissões da nota"
      @close="closePermissionView"
    />

    <EditNoteForm
      v-if="showEditModal && memberCanEditNote"
      :show="showEditModal"
      :noteId="props.noteId"
      :initialStep="editInitialStep"
      @close="handleEditClose"
    />
    <DeleteNoteForm
      v-if="showDeleteModal"
      :noteId="props.noteId"
      @close="handleDeleteClose"
      @refresh="handleDeleteRefresh"
      @notify="handleDeleteNotify"
    />
    <NoteAttachmentModal
      v-if="showAttachmentModal"
      :show="showAttachmentModal"
      :attachments="attachmentList"
      @close="closeAttachmentModal"
    />
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.note-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  max-height: calc(100vh - 40px);
  box-shadow: var(--shadow-modal);
  overflow: hidden;
  text-align: left;
  display: flex;
  flex-direction: column;
}

/* Os botões do cabeçalho continuam sendo clique, não alça */
.modal-header--draggable .modal-close-btn,
.modal-header--draggable .modal-header-actions button {
  cursor: pointer;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tooltip-bottom {
  position: relative;
}

.tooltip-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(-2px);
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 80;
}

.tooltip-bottom:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 0.8s;
}

.card-content-grid {
  display: flex;
  padding: 1rem;
  gap: 1.25rem;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.note-details-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* ✅ FIX: min-width:0 permite a coluna encolher abaixo da largura do conteúdo.
     Sem isso, uma URL longa (sem espaços) empurra a largura mínima da coluna e
     espreme a coluna da direita. Com min-width:0 as duas colunas ficam fixas
     em 50/50 e o conteúdo longo é forçado a quebrar. */
  min-width: 0;
}

.selected-text-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.note-details-inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-right: 1rem;
  border-right: 1px solid var(--border-color);
  min-height: 0;
  min-width: 0;
}

.note-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.note-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-dark);
  line-height: 1.25;
  flex: 1;
  min-width: 0;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.note-title-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  flex-shrink: 0;
  margin-left: auto;
  padding-top: 2px;
}

.btn-edit-note,
.btn-delete-note,
.btn-note-permissions,
.btn-share-modal,
.btn-attachment-note {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}

.btn-edit-note {
  border: 1px solid var(--info-border);
  background-color: var(--info-bg);
  color: var(--info-text);
}
.btn-edit-note:hover {
  background-color: var(--info-border);
  transform: translateY(-1px);
}

.btn-delete-note {
  border: 1px solid var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-text);
}
.btn-delete-note:hover {
  background-color: var(--danger-bg);
  transform: translateY(-1px);
}

.btn-note-permissions {
  border: 1px solid var(--border-color);
  background-color: var(--neutral-100);
  color: var(--text-dark);
}
.btn-note-permissions:hover {
  background-color: var(--border-color);
  color: var(--text-dark);
  transform: translateY(-1px);
}

.btn-share-modal {
  border: 1px solid var(--success-bg);
  background-color: var(--success-bg);
  color: var(--success-text);
}
.btn-share-modal:hover {
  background-color: var(--success-bg);
  transform: translateY(-1px);
}

.btn-attachment-note {
  border: 1px solid var(--warning-bg);
  background-color: var(--warning-bg);
  color: var(--warning);
}
.btn-attachment-note:hover {
  background-color: var(--warning-bg);
  color: var(--warning);
  transform: translateY(-1px);
}

.btn-edit-note i,
.btn-delete-note i,
.btn-note-permissions i,
.btn-share-modal i,
.btn-attachment-note i {
  font-size: 0.6rem;
}

.meta-grid {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  margin-bottom: 6px;
}

.meta-grid-spaced {
  gap: 12px 14px;
}

.source-inline {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.35;
  color: var(--text-muted);
  font-style: italic;
  /* ✅ FIX: mesma quebra de URLs longas na Fonte. min-width:0 no flex garante
     que o span de texto encolha e quebre em vez de estourar a coluna. */
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.source-link-icon {
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-right: 4px;
  position: relative;
  top: 5px;
}

.text-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-dark);
  margin-bottom: 0.3rem;
  display: block;
}

.note-body-blockquote {
  white-space: pre-wrap;
  line-height: 1.35;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #cbd5e0;
  background-color: #f7fafc;
  color: var(--text-dark);
  font-size: 0.9rem;
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-bottom: 12px;
  /* ✅ FIX: quebra URLs longas (sem espaços) para não estourarem a largura da
     coluna. `anywhere` permite quebrar em qualquer ponto quando não há espaço. */
  overflow-wrap: anywhere;
  word-break: break-word;
}

.content-separator {
  margin: 4px 0 10px 0;
}

.context-block {
  margin-bottom: 12px;
}

.footer-context-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
}

.meta-chip {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  padding: 1px 0;
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 500;
  background: transparent !important;
  border: none;
  opacity: 0.82;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.meta-chip-label {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.25;
}

.meta-chip-text {
  color: var(--text-muted);
}

.meta-chip-activity {
  color: #786145;
}

.meta-chip-link {
  cursor: pointer;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  text-align: left;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.meta-chip-link:hover {
  opacity: 1;
  transform: translateY(-1px);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.meta-sep {
  font-size: 0.52rem;
  color: var(--text-light);
  margin-top: 2px;
}

.footer-info {
  padding-top: 8px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 8px;
}

.note-tags-line {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 0.78rem;
  color: var(--text-light);
  min-height: 20px;
}

.tag-hash {
  font-size: 0.7rem;
  color: var(--text-muted);
  opacity: 0.8;
  margin-top: 2px;
  position: relative;
  top: 2px;
}

.tag-pill {
  display: inline;
  font-size: 0.9rem;
  line-height: 1.05;
  font-weight: 600;
  margin-left: 2px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.tag-link {
  cursor: pointer;
  border: none;
  outline: none;
  background: none;
  font-family: inherit;
  font-weight: inherit;
  padding: 1px 4px;
  border-radius: 0.5rem;
  position: relative;
}

.tag-link:hover {
  filter: brightness(0.95);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.tag-link::after {
  content: "Ver detalhes da tag";
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%) translateY(2px);
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 80;
}

.tag-link:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 0.6s;
}

.themed-blockquote {
  background-color: var(--bg-main);
  border-left: 4px solid var(--brand-light);
  color: var(--text-dark);
  padding: 1rem;
  border-radius: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  line-height: 1.4;
  box-sizing: border-box;
  font-size: 0.9rem;
  font-family: var(--font-family);
}

.context-icon {
  font-size: 0.7rem;
  margin-right: 4px;
  position: relative;
  top: 5px;
  opacity: 0.75;
}

.note-actions-row {
  display: flex;
  margin-top: 8px;
  margin-bottom: 4px;
}

.excerpt-before,
.excerpt-after {
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--text-dark);
  font-size: 0.88rem;
}

.excerpt-highlight {
  white-space: pre-wrap;
  word-break: break-word;
  padding: 0 0.25rem;
  border-radius: 0.5rem;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  transition: background-color 0.15s, color 0.15s;
  text-decoration-thickness: 1.5px !important;
  text-underline-offset: 3px !important;
}

.excerpt-ellipsis {
  color: var(--text-light);
  font-style: italic;
  font-size: 0.85rem;
  user-select: none;
}
</style>